import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { LOADING_STATES, NOTIFICATION_SEVERITY } from '../../constants';
import { setStatusBar } from '../modals/modalSlice';
import * as sharedPerimetresAPI from './sharedPerimetresAPI';
import type {
    PerimetreShare,
    SharedPerimetreView,
    NoteSharePermission
} from '../../types/noteShare';

// État initial
export interface SharedPerimetresState {
    // Périmètres que je partage (je suis propriétaire)
    ownedPerimetreShares: PerimetreShare[];
    // Périmètres partagés avec moi
    sharedWithMePerimetres: SharedPerimetreView[];
    // Périmètre partagé actif (pour affichage des notes)
    activeSharedPerimetre: SharedPerimetreView | null;
    // États de chargement
    status: string;
    sharedWithMeStatus: string;
    // Erreurs
    error: string | null;
}

const initialState: SharedPerimetresState = {
    ownedPerimetreShares: [],
    sharedWithMePerimetres: [],
    activeSharedPerimetre: null,
    status: LOADING_STATES.IDLE,
    sharedWithMeStatus: LOADING_STATES.IDLE,
    error: null
};

// =====================
// Async Thunks
// =====================

/**
 * Récupère les périmètres que je partage (je suis propriétaire)
 */
export const fetchMyPerimetreShares = createAsyncThunk(
    'sharedPerimetres/fetchMyShares',
    async (instance: any, { dispatch, rejectWithValue }) => {
        try {
            console.log('🔄 [fetchMyPerimetreShares] Chargement des périmètres partagés...');
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            
            sharedPerimetresAPI.setAuthToken(accessToken);
            const result = await sharedPerimetresAPI.getMyPerimetreShares();
            console.log('✅ [fetchMyPerimetreShares] Résultat:', result);
            return result;
        } catch (error: any) {
            console.error('❌ [fetchMyPerimetreShares] Erreur:', error);
            return rejectWithValue(error.message || 'Erreur lors du chargement');
        }
    }
);

/**
 * Récupère les périmètres partagés avec moi
 */
export const fetchSharedWithMePerimetres = createAsyncThunk(
    'sharedPerimetres/fetchSharedWithMe',
    async (instance: any, { dispatch, rejectWithValue }) => {
        try {
            console.log('🔄 [fetchSharedWithMePerimetres] Chargement des périmètres partagés avec moi...');
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            
            sharedPerimetresAPI.setAuthToken(accessToken);
            const result = await sharedPerimetresAPI.getSharedWithMePerimetres();
            console.log('✅ [fetchSharedWithMePerimetres] Résultat:', result);
            return result;
        } catch (error: any) {
            console.error('❌ [fetchSharedWithMePerimetres] Erreur:', error);
            return rejectWithValue(error.message || 'Erreur lors du chargement');
        }
    }
);

/**
 * Ajoute un participant à un périmètre partagé
 */
export const addPerimetreParticipant = createAsyncThunk(
    'sharedPerimetres/addParticipant',
    async ({ 
        perimetreId, 
        perimetreName,
        participant, 
        instance 
    }: { 
        perimetreId: string;
        perimetreName: string;
        participant: {
            userId: string;
            displayName: string;
            email?: string;
            avatar?: string;
            permission?: NoteSharePermission;
        };
        instance: any;
    }, { dispatch, rejectWithValue }) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            
            sharedPerimetresAPI.setAuthToken(accessToken);
            const result = await sharedPerimetresAPI.addParticipant(perimetreId, perimetreName, participant);
            
            dispatch(setStatusBar({
                open: true,
                message: `${participant.displayName} ajouté au dossier`,
                severity: NOTIFICATION_SEVERITY.SUCCESS
            }));
            
            return result;
        } catch (error: any) {
            dispatch(setStatusBar({
                open: true,
                message: error.message || 'Erreur lors de l\'ajout du participant',
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Retire un participant d'un périmètre partagé
 */
export const removePerimetreParticipant = createAsyncThunk(
    'sharedPerimetres/removeParticipant',
    async ({ 
        perimetreShareId, 
        participantUserId,
        participantName,
        instance 
    }: { 
        perimetreShareId: string; 
        participantUserId: string;
        participantName: string;
        instance: any;
    }, { dispatch, rejectWithValue }) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            
            sharedPerimetresAPI.setAuthToken(accessToken);
            const result = await sharedPerimetresAPI.removeParticipant(perimetreShareId, participantUserId);
            
            dispatch(setStatusBar({
                open: true,
                message: `${participantName} retiré du dossier`,
                severity: NOTIFICATION_SEVERITY.SUCCESS
            }));
            
            return { perimetreShareId, participantUserId };
        } catch (error: any) {
            dispatch(setStatusBar({
                open: true,
                message: error.message || 'Erreur lors du retrait du participant',
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Met à jour la permission d'un participant
 */
export const updatePerimetreParticipantPermission = createAsyncThunk(
    'sharedPerimetres/updateParticipantPermission',
    async ({ 
        perimetreShareId, 
        participantUserId,
        permission,
        instance 
    }: { 
        perimetreShareId: string; 
        participantUserId: string;
        permission: NoteSharePermission;
        instance: any;
    }, { dispatch, rejectWithValue }) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            
            sharedPerimetresAPI.setAuthToken(accessToken);
            const result = await sharedPerimetresAPI.updateParticipantPermission(
                perimetreShareId, 
                participantUserId, 
                permission
            );
            
            dispatch(setStatusBar({
                open: true,
                message: 'Permission mise à jour',
                severity: NOTIFICATION_SEVERITY.SUCCESS
            }));
            
            return result;
        } catch (error: any) {
            dispatch(setStatusBar({
                open: true,
                message: error.message || 'Erreur lors de la mise à jour',
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            return rejectWithValue(error.message);
        }
    }
);

// =====================
// Slice
// =====================

const sharedPerimetresSlice = createSlice({
    name: 'sharedPerimetres',
    initialState,
    reducers: {
        setActiveSharedPerimetre(state, action: PayloadAction<SharedPerimetreView | null>) {
            state.activeSharedPerimetre = action.payload;
        },
        clearSharedPerimetresError(state) {
            state.error = null;
        },
        resetSharedPerimetresState(state) {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        // fetchMyPerimetreShares
        builder
            .addCase(fetchMyPerimetreShares.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(fetchMyPerimetreShares.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                state.ownedPerimetreShares = action.payload;
            })
            .addCase(fetchMyPerimetreShares.rejected, (state, action) => {
                state.status = LOADING_STATES.FAILED;
                state.error = action.payload as string;
            });
        
        // fetchSharedWithMePerimetres
        builder
            .addCase(fetchSharedWithMePerimetres.pending, (state) => {
                state.sharedWithMeStatus = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(fetchSharedWithMePerimetres.fulfilled, (state, action) => {
                state.sharedWithMeStatus = LOADING_STATES.SUCCEEDED;
                state.sharedWithMePerimetres = action.payload;
            })
            .addCase(fetchSharedWithMePerimetres.rejected, (state, action) => {
                state.sharedWithMeStatus = LOADING_STATES.FAILED;
                state.error = action.payload as string;
            });
        
        // addPerimetreParticipant
        builder
            .addCase(addPerimetreParticipant.fulfilled, (state, action) => {
                // Mettre à jour le périmètre dans la liste
                const index = state.ownedPerimetreShares.findIndex(
                    p => p.perimetreId === action.payload.perimetreId
                );
                if (index !== -1) {
                    state.ownedPerimetreShares[index] = action.payload;
                } else {
                    state.ownedPerimetreShares.push(action.payload);
                }
            });
        
        // removePerimetreParticipant
        builder
            .addCase(removePerimetreParticipant.fulfilled, (state, action) => {
                const { perimetreShareId, participantUserId } = action.payload;
                const share = state.ownedPerimetreShares.find(p => p._id === perimetreShareId);
                if (share) {
                    share.participants = share.participants.filter(
                        p => p.userId !== participantUserId
                    );
                }
            });
        
        // updatePerimetreParticipantPermission
        builder
            .addCase(updatePerimetreParticipantPermission.fulfilled, (state, action) => {
                const index = state.ownedPerimetreShares.findIndex(
                    p => p._id === action.payload._id
                );
                if (index !== -1) {
                    state.ownedPerimetreShares[index] = action.payload;
                }
            });
    }
});

// Actions
export const {
    setActiveSharedPerimetre,
    clearSharedPerimetresError,
    resetSharedPerimetresState
} = sharedPerimetresSlice.actions;

// Sélecteurs
export const selectOwnedPerimetreShares = (state: any) => state.sharedPerimetres.ownedPerimetreShares;
export const selectSharedWithMePerimetres = (state: any) => state.sharedPerimetres.sharedWithMePerimetres;
export const selectActiveSharedPerimetre = (state: any) => state.sharedPerimetres.activeSharedPerimetre;
export const selectSharedPerimetresStatus = (state: any) => state.sharedPerimetres.status;
export const selectSharedWithMeStatus = (state: any) => state.sharedPerimetres.sharedWithMeStatus;
export const selectSharedPerimetresError = (state: any) => state.sharedPerimetres.error;

export default sharedPerimetresSlice.reducer;
